import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const MUTUACORE_API_URL = process.env.REACT_APP_MUTUACORE_API_URL || 'https://sso.mutuacore.fr';

// Génère un mot de passe fort : au moins une majuscule, une minuscule,
// un chiffre et un caractère spécial, 12 caractères.
function genererMotDePasseFort() {
  const majuscules = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const minuscules = 'abcdefghijkmnpqrstuvwxyz';
  const chiffres = '23456789';
  const speciaux = '!@#$%^&*-_+=';
  const tous = majuscules + minuscules + chiffres + speciaux;

  let mdp = [
    majuscules[Math.floor(Math.random() * majuscules.length)],
    minuscules[Math.floor(Math.random() * minuscules.length)],
    chiffres[Math.floor(Math.random() * chiffres.length)],
    speciaux[Math.floor(Math.random() * speciaux.length)],
  ];

  for (let i = mdp.length; i < 12; i++) {
    mdp.push(tous[Math.floor(Math.random() * tous.length)]);
  }

  // On mélange pour ne pas avoir un motif prévisible (majuscule toujours en 1ère position, etc.)
  for (let i = mdp.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [mdp[i], mdp[j]] = [mdp[j], mdp[i]];
  }

  return mdp.join('');
}

// Icônes œil / œil barré en SVG inline (pas de dépendance à installer)
const IconeOeil = ({ visible }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7f8c8d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {visible ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

function ChampMotDePasse({ label, value, onChange, placeholder }) {
  const [visible, setVisible] = useState(false);

  return (
    <div style={{ marginBottom: '15px' }}>
      <label style={{ display: 'block', marginBottom: '5px', color: '#34495e', fontWeight: 'bold' }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={visible ? 'text' : 'password'}
          required
          value={value}
          onChange={onChange}
          style={{ width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '5px', border: '1px solid #ccc', boxSizing: 'border-box' }}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
        >
          <IconeOeil visible={visible} />
        </button>
      </div>
    </div>
  );
}

function ReinitialiserMotDePasse() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token');

  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [succes, setSucces] = useState(false);
  const [chargement, setChargement] = useState(false);

  const regleValide = {
    longueur: motDePasse.length >= 8,
    majuscule: /[A-Z]/.test(motDePasse),
    minuscule: /[a-z]/.test(motDePasse),
    chiffre: /[0-9]/.test(motDePasse),
    special: /[^A-Za-z0-9]/.test(motDePasse),
  };
  const motDePasseValide = Object.values(regleValide).every(Boolean);

  const handleGenerer = () => {
    const genere = genererMotDePasseFort();
    setMotDePasse(genere);
    setConfirmation(genere);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!motDePasseValide) {
      setError('Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.');
      return;
    }
    if (motDePasse !== confirmation) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setChargement(true);
    try {
      const response = await fetch(`${MUTUACORE_API_URL}/email/reset-password/confirmer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, nouveau_mot_de_passe: motDePasse })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSucces(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(data.detail || 'Lien invalide ou expiré. Veuillez refaire une demande.');
      }
    } catch (err) {
      console.error('Erreur réinitialisation:', err);
      setError('Erreur de connexion au serveur sécurisé. Veuillez réessayer.');
    } finally {
      setChargement(false);
    }
  };

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ecf0f1', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px', textAlign: 'center' }}>
          <h2 style={{ color: '#2c3e50', margin: '0 0 10px 0' }}>Audit Cyber Pro</h2>
          <div style={{ background: '#e74c3c', color: 'white', padding: '10px', borderRadius: '5px', margin: '20px 0', fontSize: '14px' }}>
            Lien invalide ou incomplet.
          </div>
          <Link to="/mot-de-passe-oublie" style={{ color: '#3498db', textDecoration: 'none', fontWeight: 'bold' }}>
            Refaire une demande de réinitialisation
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ecf0f1', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: 'white', padding: '40px', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h2 style={{ color: '#2c3e50', margin: '0 0 10px 0' }}>Audit Cyber Pro</h2>
          <p style={{ color: '#7f8c8d', margin: 0 }}>Nouveau mot de passe</p>
        </div>

        {error && (
          <div style={{ background: '#e74c3c', color: 'white', padding: '10px', borderRadius: '5px', marginBottom: '20px', textAlign: 'center', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {succes ? (
          <div style={{ background: '#2ecc71', color: 'white', padding: '15px', borderRadius: '5px', textAlign: 'center', fontSize: '14px' }}>
            Mot de passe réinitialisé avec succès. Redirection vers la connexion...
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <ChampMotDePasse
              label="Nouveau mot de passe"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              placeholder="••••••••"
            />
            <ChampMotDePasse
              label="Confirmer le mot de passe"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="••••••••"
            />

            <button
              type="button"
              onClick={handleGenerer}
              style={{ width: '100%', padding: '10px', background: 'white', color: '#2c3e50', border: '1px solid #2c3e50', borderRadius: '5px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', marginBottom: '15px' }}
            >
              Générer un mot de passe fort
            </button>

            <ul style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '20px', paddingLeft: '18px' }}>
              <li style={{ color: regleValide.longueur ? '#2ecc71' : '#7f8c8d' }}>Au moins 8 caractères</li>
              <li style={{ color: regleValide.majuscule ? '#2ecc71' : '#7f8c8d' }}>Une majuscule</li>
              <li style={{ color: regleValide.minuscule ? '#2ecc71' : '#7f8c8d' }}>Une minuscule</li>
              <li style={{ color: regleValide.chiffre ? '#2ecc71' : '#7f8c8d' }}>Un chiffre</li>
              <li style={{ color: regleValide.special ? '#2ecc71' : '#7f8c8d' }}>Un caractère spécial</li>
            </ul>

            <button
              type="submit"
              disabled={chargement}
              style={{ width: '100%', padding: '12px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '5px', fontWeight: 'bold', fontSize: '16px', cursor: chargement ? 'not-allowed' : 'pointer', opacity: chargement ? 0.7 : 1 }}
            >
              {chargement ? 'Enregistrement...' : 'Réinitialiser le mot de passe'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ReinitialiserMotDePasse;